import React, { useEffect, useState } from "react";
import axios from "../utils/axiosInstance";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Typography,
  Chip,
  Avatar,
  Button,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Folder,
  InsertDriveFile,
  CloudDownload,
  ArrowUpward,
  CloudUpload,
  Close,
} from "@mui/icons-material";
import Layout from "./Layout";
import { useAuth } from "../utils/AuthContext";
import StructuredFileUpload from "./StructuredFileUploadButton";
import { formatDate } from "../utils/dateUtils";

const FileList = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [filters, setFilters] = useState({ name: "", type: "" });
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [userSorted, setUserSorted] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const path = currentPath.join("/");
        const response = await axios.get(`/file/files?path=${path}`);
        setFiles(response.data.contents);
      } catch (error) {
        console.error("Error fetching files:", error);
      }
    };
    fetchFiles();
  }, [currentPath]);

  const handleSort = (key) => {
    setUserSorted(true);
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedFiles = [...files].sort((a, b) => {
    if (!userSorted) {
      // Folders first by default
      if (a.type === "folder" && b.type !== "folder") return -1;
      if (a.type !== "folder" && b.type === "folder") return 1;
    }

    if (a[sortConfig.key] < b[sortConfig.key])
      return sortConfig.direction === "asc" ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key])
      return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const filteredFiles = sortedFiles.filter((file) => {
    return (
      file.name.toLowerCase().includes(filters.name.toLowerCase()) &&
      (filters.type === "" || file.type === filters.type)
    );
  });

  const showNotification = (message, severity = "info") => {
    setNotification({ open: true, message, severity });
  };

  const handleDownload = async (filePath, fileName) => {
    // Add file to downloading set
    setDownloadingFiles((prev) => new Set(prev).add(filePath));

    try {
      // Prepare comprehensive log data
      const logData = {
        timestamp: new Date().toISOString(),
        user: {
          username: user?.username || "unknown",
          userId: user?.id || null,
          manufacturer: user?.manufacturer || "unknown",
        },
        file: {
          fileName: fileName,
          filePath: filePath,
          downloadStartTime: new Date().toISOString(),
        },
        download: {
          path: currentPath.join("/"),
          downloadStartTime: new Date().toISOString(),
        },
      };

      // Log to backend before download
      try {
        await axios.post("/file/log-download", logData);
      } catch (logError) {
        // Continue with download even if logging fails
      }

      // Perform the download
      const response = await axios.get(`/file/download/${filePath}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Log successful download completion
      try {
        await axios.post("/file/log-download-completion", {
          logId: logData.timestamp,
          downloadEndTime: new Date().toISOString(),
          downloadSuccess: true,
          fileName: fileName,
          filePath: filePath,
        });
      } catch (completionLogError) {
        console.error("Failed to log download completion:", completionLogError);
      }

      showNotification(`Successfully downloaded ${fileName}`, "success");
    } catch (error) {
      console.error("Download failed:", error);

      // Log failed download
      try {
        await axios.post("/file/log-download-completion", {
          logId: new Date().toISOString(),
          downloadEndTime: new Date().toISOString(),
          downloadSuccess: false,
          error: error.message || "Unknown error",
          fileName: fileName,
          filePath: filePath,
        });
      } catch (failureLogError) {
        console.error("Failed to log download failure:", failureLogError);
      }

      showNotification(`Failed to download ${fileName}`, "error");
    } finally {
      // Remove file from downloading set
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(filePath);
        return newSet;
      });
    }
  };

  const handleUploadOpen = () => setUploadOpen(true);
  const handleUploadClose = () => {
    setUploadOpen(false);
    setSelectedFiles([]);
    setUploadProgress(0);
    setUploadError(null);
  };

  const handleFileSelect = (e) => {
    setUploadError(null);
    setSelectedFiles([...e.target.files]);
  };

  const handleFileUpload = async () => {
    setUploadError(null);
    const formData = new FormData();
    formData.append("path", currentPath.join("/"));

    // Client-side validation
    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = [
      "png",
      "jpg",
      "jpeg",
      "gif",
      "pdf",
      "txt",
      "xlsx",
      "csv",
    ]; // Match backend config

    const validationErrors = [];

    selectedFiles.forEach((file) => {
      // Check file size
      if (file.size > maxSize) {
        validationErrors.push(`File '${file.name}' exceeds 50MB limit`);
      }

      // Check file extension
      const extension = file.name.split(".").pop().toLowerCase();
      if (!allowedTypes.includes(extension)) {
        validationErrors.push(`File '${file.name}' has invalid type`);
      }

      formData.append("files", file, file.name);
    });

    if (validationErrors.length > 0) {
      setUploadError({
        type: "client",
        messages: validationErrors,
      });
      return;
    }

    try {
      const response = await axios.post("/file/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percent);
        },
      });

      if (response.data.error) {
        setUploadError({
          type: "server",
          messages: response.data.details || [response.data.error],
        });
      } else {
        setCurrentPath([...currentPath]);
        handleUploadClose();
        showNotification("Files uploaded successfully", "success");
      }
    } catch (error) {
      let errorMessages = [];
      setUploadProgress(0);
      // Handle NGINX 413 error
      if (error.response?.status === 413) {
        errorMessages.push("File(s) too large - maximum total size is 50MB");
      }
      // Handle backend validation errors
      else if (error.response?.data?.details) {
        errorMessages = error.response.data.details;
      }
      // Handle HTML responses from proxy errors
      else if (
        error.response?.headers?.["content-type"]?.includes("text/html")
      ) {
        errorMessages.push(
          "Server error - please check file size and try again"
        );
      } else {
        errorMessages.push("Upload failed - please try again");
      }

      setUploadError({
        type: "server",
        messages: errorMessages,
      });
    }
  };

  return (
    <Layout>
      <Box
        sx={{
          p: 3,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          margin: 0,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ flexShrink: 0 }}>
            <Link
              component="button"
              onClick={() => setCurrentPath([])}
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                "&:hover": { color: theme.palette.primary.main },
              }}
            >
              Shared Files
            </Link>
            {currentPath.map((segment, index) => (
              <Link
                key={index}
                component="button"
                onClick={() =>
                  setCurrentPath((prev) => prev.slice(0, index + 1))
                }
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": { color: theme.palette.primary.main },
                }}
              >
                {segment}
              </Link>
            ))}
          </Breadcrumbs>

          {/* Controls Container */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexGrow: 1,
              justifyContent: "flex-end",
              minWidth: 300,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexGrow: 1,
                maxWidth: 600,
                mr: 2,
              }}
            >
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Search files..."
                value={filters.name}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, name: e.target.value }))
                }
                sx={{
                  flexShrink: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, type: e.target.value }))
                  }
                  label="Type"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="folder">Folders</MenuItem>
                  <MenuItem value="file">Files</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {user?.manufacturer === "ciita" && (
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={handleUploadOpen}
                sx={{
                  height: 40,
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                Upload
              </Button>
            )}
            <StructuredFileUpload
              currentPath={currentPath}
              onUpload={() => setCurrentPath([...currentPath])}
            />
          </Box>
        </Box>

        {/* Table Container */}
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
            overflow: "auto",
            maxHeight: "calc(100vh - 180px)",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "50%" }}>
                  <TableSortLabel
                    active={sortConfig.key === "name"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  <TableSortLabel
                    active={sortConfig.key === "type"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("type")}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  <TableSortLabel
                    active={sortConfig.key === "modified"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("modified")}
                  >
                    Modified
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: "10%" }}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {currentPath.length > 0 && (
                <TableRow
                  hover
                  onClick={() => setCurrentPath((prev) => prev.slice(0, -1))}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ArrowUpward sx={{ mr: 1 }} />
                      Parent Folder
                    </Box>
                  </TableCell>
                  <TableCell>Folder</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}

              {filteredFiles.map((file, index) => (
                <TableRow
                  key={index}
                  hover
                  onClick={() =>
                    file.type === "folder" &&
                    setCurrentPath((prev) => [...prev, file.name])
                  }
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {file.type === "folder" ? (
                        <Folder
                          sx={{ mr: 1, color: theme.palette.text.secondary }}
                        />
                      ) : (
                        <InsertDriveFile
                          sx={{ mr: 1, color: theme.palette.text.secondary }}
                        />
                      )}
                      {file.name}
                    </Box>
                  </TableCell>
                  <TableCell>{file.type}</TableCell>
                  <TableCell>{formatDate(file.modified)}</TableCell>
                  <TableCell>
                    {file.type !== "folder" && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(file.path, file.name);
                        }}
                        disabled={downloadingFiles.has(file.path)}
                        sx={{ color: theme.palette.primary.main }}
                      >
                        {downloadingFiles.has(file.path) ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudDownload />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Upload Dialog */}
        <Dialog
          open={uploadOpen}
          onClose={handleUploadClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: `1px solid ${theme.palette.divider}`,
              py: 2,
            }}
          >
            <CloudUpload sx={{ mr: 1.5, color: theme.palette.primary.main }} />
            <Typography variant="h6">Upload Files</Typography>
            <IconButton
              onClick={handleUploadClose}
              sx={{ ml: "auto", color: theme.palette.text.secondary }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ py: 4 }}>
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                const files = Array.from(e.dataTransfer.files);
                setSelectedFiles(files);
              }}
              sx={{
                border: `2px dashed ${
                  dragActive
                    ? theme.palette.primary.main
                    : theme.palette.divider
                }`,
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                mb: 3,
                backgroundColor: theme.palette.background.paper,
                transition: "border-color 0.3s",
                "&:hover": {
                  borderColor: dragActive
                    ? theme.palette.primary.main
                    : theme.palette.divider,
                },
              }}
            >
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: "none" }}
                id="file-upload-input"
              />
              <label htmlFor="file-upload-input">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  sx={{ mb: 2 }}
                >
                  Choose Files
                </Button>
              </label>
              <Typography variant="body2" color="textSecondary">
                or drag and drop files here
              </Typography>
            </Box>

            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Files ({selectedFiles.length}):
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    maxHeight: 200,
                    overflow: "auto",
                    p: 1,
                  }}
                >
                  {selectedFiles.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      onDelete={() => {
                        setUploadError(null);
                        setSelectedFiles((f) =>
                          f.filter((_, i) => i !== index)
                        );
                      }}
                      sx={{
                        borderRadius: 1,
                        "& .MuiChip-deleteIcon": { fontSize: 18 },
                      }}
                      avatar={
                        <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
                          <InsertDriveFile fontSize="small" />
                        </Avatar>
                      }
                    />
                  ))}
                </Box>
              </Box>
            )}

            {uploadProgress > 0 && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}

            {uploadError && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: theme.palette.error.light,
                  borderRadius: 1,
                  color: theme.palette.error.contrastText,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Upload Issues:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {uploadError.messages.map((msg, index) => (
                    <li key={index}>
                      <Typography variant="body2">{msg}</Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              onClick={handleUploadClose}
              sx={{ color: theme.palette.text.secondary }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleFileUpload}
              disabled={selectedFiles.length === 0 || uploadProgress > 0}
              sx={{ borderRadius: 2 }}
            >
              Start Upload
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setNotification({ ...notification, open: false })}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default FileList;
